/**
 * Enter moves to the next control, the way every ERP data-entry screen has worked for thirty years.
 *
 * These forms are filled in at a counter, from a paper slip, by someone who is not looking at the
 * screen — and on a browser's default rules Enter *submits*, so a half-typed booking is saved by
 * the same keystroke that finishes a field. Tab is the web's answer and it is the wrong shape for
 * this: it is a stretch on the left hand while the right hand is on the number pad, and the
 * numeric keys and Enter are the two things a counter keyboard is used through.
 *
 * So Enter advances, Shift+Enter goes back, and **the last field hands focus to Save rather than
 * saving**. That last part is the one deliberate departure from "Enter just submits at the end":
 * committing is worth one more keystroke and a visible focus ring on the button that does it,
 * because a form reached by Enter-ing through fourteen fields is exactly the one nobody re-reads.
 *
 * What Enter must keep meaning, and therefore what this leaves alone:
 *
 * - **A textarea**, where Enter is a newline. Same for anything contenteditable.
 * - **A button or a link**, where Enter is a click — including the Save button itself, which is how
 *   the second keystroke above gets to submit the form.
 * - **An open dropdown or calendar**, where Enter picks the highlighted option. Ant Design's Select
 *   and DatePicker both write `aria-expanded` on the control they own, so one attribute covers
 *   every popup in the app rather than a list of class names that goes stale.
 * - **A form with a single control**, where there is no next field and no previous one, so the only
 *   thing Enter can sensibly mean is submit. This is what keeps a one-field form — a code, a
 *   verification digit — behaving the way anyone would expect.
 *
 * Attach it to the `<form>` and it covers every control inside by bubbling; `FormDrawer` does this
 * once for all eleven drawers. It is deliberately **not** on the sign-in, two-factor and
 * change-password screens: those are not data entry, and "type the password, press Enter" is
 * muscle memory that predates every ERP this imitates.
 *
 * **The container does not have to be a `<form>`, and several screens are why.** The two full-page
 * counters (`NewBookingPage`, `StockIntakePage`) and the four hand-rolled panels (`PaymentPage`,
 * `GarmentStateDrawer`, `IssueCounter`, `ReturnCounter`) have no form element and cannot grow one:
 * each of them renders a `FormDrawer` as a descendant, and a nested `<form>` is invalid HTML whose
 * submit button submits the outer one. They attach this to their root `<div>` instead, which costs
 * exactly one thing — there is no `button[type="submit"]` for the last field to hand focus to — so
 * such a screen marks its commit button `data-enter-key-commit` and gets the same handoff. It is an
 * explicit opt-in rather than "the first primary button": a page carries several, and guessing
 * wrong sends the last field to *Cancel*.
 */

import { useCallback, useRef, type RefCallback } from 'react';

import { useMediaQuery } from './useMediaQuery';

/**
 * Everything that counts as a field for the purpose of "the next one".
 *
 * `button[role="switch"]` is Ant Design's `Switch` — a button, not a checkbox, so it needs naming
 * here or a form's toggles would be skipped over on the way down. Space still toggles it, which is
 * a button's own behaviour and the reason Enter is free to mean "next".
 */
const NAVIGABLE_SELECTOR = [
  'input:not([type="hidden"])',
  'textarea',
  'select',
  'button[role="switch"]',
  '[contenteditable="true"]',
].join(', ');

/**
 * Input types where arriving selects what is already there, so typing replaces rather than appends
 * — the ERP habit this exists to reproduce.
 *
 * `number` and `email` are left out on purpose: `select()` is only defined for inputs that support
 * a text selection, and those two do not. Ant Design's `InputNumber` is unaffected, because it
 * renders an untyped input (so, a text one) with `role="spinbutton"` rather than a native
 * `type="number"`.
 */
const SELECTABLE_INPUT_TYPES = new Set(['text', 'search', 'url', 'tel', 'password']);

/**
 * Marks a subtree whose controls mean something else by Enter — `CustomerPicker` picks the
 * highlighted customer, `AddWearer` commits a wearer.
 *
 * <p>
 * They used to be covered by the `defaultPrevented` check: they called `preventDefault` and this
 * handler, running on the bubble, saw it and stood down. That stopped being a rule and became an
 * accident of ordering once the handler moved to the capture phase — where it now has to run,
 * because Ant Design's `Select` <em>also</em> calls `preventDefault` on Enter (to open its
 * dropdown), which was silently killing Enter-to-next-field in every drawer that has one.
 * </p>
 *
 * <p>
 * So ownership is declared rather than inferred, the same way `data-enter-key-commit` is: a screen
 * that wants Enter says so, and everything else advances.
 * </p>
 */
export const ENTER_OWNER_ATTRIBUTE = 'data-enter-key-owns';

/**
 * The part of a keyboard event this needs.
 *
 * Structural rather than `React.KeyboardEvent`, so the same function serves a React `onKeyDown` and
 * a plain `addEventListener` — which is what lets the tests dispatch real events at a real form
 * instead of hand-rolling a synthetic one.
 */
export interface EnterKeyEvent {
  readonly key: string;
  readonly shiftKey: boolean;
  readonly ctrlKey: boolean;
  readonly altKey: boolean;
  readonly metaKey: boolean;
  readonly defaultPrevented: boolean;
  readonly target: EventTarget | null;
  readonly currentTarget: EventTarget | null;
  preventDefault(): void;
}

export function handleEnterKeyNavigation(event: EnterKeyEvent): void {
  if (event.key !== 'Enter' || event.defaultPrevented) {
    return;
  }

  // Ctrl/Cmd+Enter is the conventional "submit from anywhere" shortcut and Alt+Enter belongs to the
  // browser. Shift is the exception — it is this module's own "go back" and is handled below.
  if (event.ctrlKey || event.altKey || event.metaKey) {
    return;
  }

  const form = event.currentTarget;
  const source = event.target;

  if (!(form instanceof HTMLElement) || !(source instanceof HTMLElement)) {
    return;
  }

  if (!isNavigationSource(source)) {
    return;
  }

  const controls = navigableControls(form);

  // One control means no next field and no previous one. Leave the event alone so the browser's own
  // implicit submission still happens.
  if (controls.length < 2) {
    return;
  }

  const index = controls.indexOf(source);

  if (index < 0) {
    return;
  }

  const destination = event.shiftKey
    ? (controls[index - 1] ?? null)
    : // Past the last field, Save is the next thing in the sequence. If a form has no submit button
      // there is nothing to move to and the event is left alone, so a form that submits implicitly
      // still can.
      (controls[index + 1] ?? commitControl(form));

  if (!destination) {
    // Only reachable going backwards from the first control. Swallow it rather than letting it
    // submit — Shift+Enter has never meant "save" anywhere.
    if (event.shiftKey) {
      event.preventDefault();
    }

    return;
  }

  // Before focusing, not after: this is what stops the browser submitting the form, and the whole
  // point of the module is that finishing a field is not the same act as committing the record.
  event.preventDefault();
  focusControl(destination);
}

/**
 * Put the cursor in the first field the moment a form appears.
 *
 * <p>
 * The last piece of "the mouse is never required": Enter walks the fields and Alt+S commits, but
 * both of those need the cursor to be *somewhere* first, and a drawer that opens with focus on
 * `<body>` costs a click before either can start. Every data-entry surface in the product does this,
 * so it is one rule rather than an `autoFocus` on whichever field each screen's author remembered.
 * </p>
 *
 * <p>
 * <strong>Not on a touch screen</strong>, which is the one deliberate exception. Focusing a field
 * raises the on-screen keyboard, and a keyboard covering half the drawer the instant it opens is
 * worse than a tap — there is no physical Enter key there for the focus to be leading towards. The
 * test is `pointer: fine`, the pointing device itself, rather than a width: a 1280px tablet at a
 * counter is a touch screen and a small laptop window is not.
 * </p>
 *
 * <p>
 * The scope is the element the fields live in, so a drawer hands in its scrolling body rather than
 * its whole chrome — otherwise "first field" could be the close button's neighbour rather than the
 * first thing to type in.
 * </p>
 *
 * <p>
 * <strong>A ref callback, not an effect on a ref object, and that was a real bug rather than a
 * preference.</strong> Most of these forms are inside a `{data && …}` guard, so on mount there is no
 * element and no fields — an effect that runs once at mount finds a null ref, returns, and never
 * gets another chance. The company profile and settings screens both did exactly that and focused
 * nothing. A ref callback fires when the node *attaches*, which is the moment the data arrived.
 * </p>
 */
export function useAutoFocusFirstField<T extends HTMLElement = HTMLElement>(
  active = true,
): RefCallback<T> {
  // Which node has already been given the cursor. Without it, any re-render that changes the
  // callback's identity would focus again — pulling the cursor out of whatever is being typed.
  const done = useRef<T | null>(null);
  const keyboardDriven = useMediaQuery('(pointer: fine)');

  return useCallback(
    (node: T | null) => {
      if (!node) {
        // Detaching. Cleared so a drawer reopened onto a fresh node focuses again.
        done.current = null;

        return;
      }

      if (!active || !keyboardDriven || done.current === node) {
        return;
      }

      done.current = node;
      focusFirstField(node);
    },
    [active, keyboardDriven],
  );
}

/**
 * The first field in a scope, focused. Returns whether there was one.
 *
 * <p>
 * <strong>The scope may be the field itself, and that is not a convenience.</strong>
 * `navigableControls` searches descendants, so a ref attached straight to an `<input>` matched
 * nothing and focused nothing — silently, since the caller looks identical either way. Four pages
 * had wired `useAutoFocusFirstField` that way (`CountDrawerPage`, `CashAccountPage`,
 * `NewExpensePage`, `ExtendBookingPage`) and every one of them opened with the cursor nowhere, on
 * counter screens whose whole argument is that the mouse is never required.
 * </p>
 *
 * <p>
 * Both shapes are legitimate — `FormDrawer` hands this a container holding only the fields, because
 * its commit button lives outside and must not be what the cursor lands on — so the fix is to
 * accept either rather than to make one of them wrong. A container is never navigable itself, so
 * checking it first changes nothing for the drawers.
 * </p>
 */
export function focusFirstField(scope: HTMLElement): boolean {
  const first =
    isReachable(scope) && scope.matches(NAVIGABLE_SELECTOR) ? scope : navigableControls(scope)[0];

  if (!first) {
    return false;
  }

  focusControl(first);

  return true;
}

/**
 * What a container that is not a `<form>` spreads onto itself to join the sequence.
 *
 * Two things that must never be applied separately, which is why they are one object: the handler
 * makes Enter move, and the attribute is how a control *inside* finds the boundary of the sequence
 * it belongs to (see `focusAfter`). A `<form>` needs neither — it is its own boundary and
 * `FormDrawer` attaches the handler directly.
 */
export const enterKeyScope = {
  onKeyDown: handleEnterKeyNavigation,
  'data-enter-key-scope': '',
} as const;

/**
 * Move focus to the first field after `anchor`, as though Enter had been pressed inside it.
 *
 * <p>
 * <strong>`CustomerPicker` is why this is exported.</strong> Choosing a customer replaces the search
 * box with a card naming who was chosen, so the control that had focus stops existing — and focus
 * falls to `<body>`, on the one screen whose whole argument is that a clerk never leaves the
 * keyboard. It cannot press Enter on its own behalf either: by the time React has re-rendered, the
 * element to move *from* is gone. So it hands in an element that outlives the swap and this walks on
 * from there, which is also why the answer is "the next field after this subtree" rather than "the
 * field after this control".
 * </p>
 *
 * <p>
 * The scope is the nearest `<form>` or `data-enter-key-scope` — the same element the keydown handler
 * is attached to. Without it the walk would run off the end of a drawer and land in the page behind,
 * which is a worse outcome than not moving at all. Returns whether it found somewhere to go.
 * </p>
 */
export function focusAfter(anchor: HTMLElement): boolean {
  const scope = anchor.closest<HTMLElement>('form, [data-enter-key-scope]');

  if (!scope) {
    return false;
  }

  const destination = navigableControls(scope).find(
    (control) =>
      // Following *and* outside: `compareDocumentPosition` calls a descendant "following" too, and
      // an anchor's own controls are exactly what the caller is moving on from.
      !anchor.contains(control) &&
      (anchor.compareDocumentPosition(control) & Node.DOCUMENT_POSITION_FOLLOWING) !== 0,
  );

  if (!destination) {
    return false;
  }

  focusControl(destination);

  return true;
}

/** Whether Enter pressed *on* this element means "next field" rather than something of its own. */
function isNavigationSource(element: HTMLElement): boolean {
  if (element instanceof HTMLTextAreaElement || element.isContentEditable) {
    return false;
  }

  // The `switch` exception is Ant Design's `Switch`; every other button — Save, Cancel, the drawer's
  // close — keeps Enter as its click.
  if (element instanceof HTMLButtonElement && element.getAttribute('role') !== 'switch') {
    return false;
  }

  if (element instanceof HTMLAnchorElement) {
    return false;
  }

  // An open Select or DatePicker owns the key: Enter picks the highlighted option, and stealing it
  // would make a dropdown impossible to use from the keyboard.
  if (element.getAttribute('aria-expanded') === 'true') {
    return false;
  }

  // A control that means something else by Enter, declared out loud. See `ENTER_OWNER_ATTRIBUTE`.
  if (element.closest(`[${ENTER_OWNER_ATTRIBUTE}]`)) {
    return false;
  }

  return element.matches(NAVIGABLE_SELECTOR);
}

function navigableControls(form: HTMLElement): HTMLElement[] {
  return Array.from(form.querySelectorAll<HTMLElement>(NAVIGABLE_SELECTOR)).filter(isReachable);
}

/**
 * Whether a control can be landed on.
 *
 * Read-only is deliberately not disqualifying: a non-searchable Ant Design `Select` renders its
 * focus target as a read-only input, and skipping those would step over every dropdown on the form.
 */
function isReachable(element: HTMLElement): boolean {
  if (element.matches(':disabled') || element.getAttribute('aria-disabled') === 'true') {
    return false;
  }

  if (element.tabIndex < 0) {
    return false;
  }

  return !element.closest('[hidden], [inert], [aria-hidden="true"]');
}

/**
 * The control the last field hands focus to — Save, or whatever this screen calls committing.
 *
 * A `<form>` says which button that is by declaring one `type="submit"`; a container that is not a
 * form has no such word, so it says so with `data-enter-key-commit`. Reachability is checked rather
 * than assumed because these buttons are routinely disabled until the record is valid, and landing
 * on a disabled button strands the keyboard on a control that cannot be pressed or moved on from.
 */
function commitControl(container: HTMLElement): HTMLElement | null {
  const candidates = container.querySelectorAll<HTMLElement>(
    'button[type="submit"], [data-enter-key-commit]',
  );

  return Array.from(candidates).find(isReachable) ?? null;
}

function focusControl(element: HTMLElement): void {
  element.focus();

  if (element instanceof HTMLInputElement && SELECTABLE_INPUT_TYPES.has(element.type)) {
    element.select();
  }
}
